/**
 * Shared "presence, not just value" write-back rule for an OPTIONAL numeric
 * field that a geometry transform (MOVE / ROTATE / SCALE / MIRROR) has just
 * recomputed, where the field's ABSENCE carries a well-known implicit
 * default (never a `.default()` in the Zod schema — the DXF group is
 * genuinely OMITTED when the source never had it, distinct from an explicit
 * value that happens to equal the default).
 *
 * Rule: an ALREADY-PRESENT field (any prior value, including one that
 * happens to equal the implicit default) is written back EXPLICITLY
 * regardless of what it transforms to — even when the transformed value
 * happens to equal the implicit default again. Only a field that was
 * GENUINELY ABSENT before the call, and that transforms to the SAME
 * implicit default it was already implying, stays absent — never invent a
 * group the source never had, never discard one the source explicitly did.
 *
 * Returns the value to WRITE, or `undefined` to mean "leave the field
 * exactly as it was" (the caller must NOT assign a key when this returns
 * `undefined`, or it would explicitly re-materialize `undefined` — for a
 * plain object spread that is harmless (an explicit `undefined` value still
 * reads back as absent via `in`/`Object.keys` on most call sites here,
 * but every caller instead skips the assignment entirely to keep the
 * source object reference-stable when nothing changed).
 */
export function presencePreservingValue(
  currentValue: number | undefined,
  transformedValue: number,
  impliedDefault: number,
): number | undefined {
  if (transformedValue === impliedDefault && currentValue === undefined) {
    return undefined;
  }
  return transformedValue;
}

/**
 * `presencePreservingValue` specialised to the `impliedDefault = 0` case —
 * every bare rotation-angle field this codebase carries (`overrideRotation`,
 * `mTextData.rotation`) implies 0 when absent (AutoCAD's own default
 * rotation). Extracted (MIRRTEXT-DIMENSION-MLEADER-ATTRIB, local Codex
 * read-only review, round 4) from `blockAttributeRotation.ts`'s
 * `withWorldAttributeRotation` once a SECOND rotation call site needed the
 * exact same rule — Multileader `secondaryContexts[].context.mTextData
 * .rotation` (round-4 finding 2) is a different field on a different entity
 * type, but the byte-faithfulness contract is identical to a block
 * attribute's WORLD `overrideRotation` (round-3 finding): both fields are
 * `.optional()` (never `.default()`).
 */
export function presencePreservingRotation(
  currentValue: number | undefined,
  transformedValue: number,
): number | undefined {
  return presencePreservingValue(currentValue, transformedValue, 0);
}
